import moment from 'moment';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { ChanelConfig } from '../../../configs/GlobalConfig';
import { listLanguage } from '../../../configs/LanguageConfig';
import { THEMES } from '../../../configs/LayoutConfig';
import MessageHub from '../../../core/signalr/SignalrMessageHub';
import { REDUCER_NAME as COMMON_REDUCER_NAME } from '../../app/commonReducer';
import { ChartCanvas } from '../../common/chart';
import { Chart } from 'react-stockcharts';
import { scalePoint, scaleLinear } from 'd3-scale';
import { XAxis, YAxis } from 'react-stockcharts/lib/axes';
import { I18n } from 'react-redux-i18n';
import { AreaSeries } from 'react-stockcharts/lib/series';
import LineSeries from '../../common/chart/LineSeries';
import HoverTooltip from '../../common/chart/lib/tooltip/HoverTooltip';
import { isBetweenTime } from '../../helpers/Common';
import { formatPrice, formatTextFloat, formatVolume } from '../../helpers/Text';
import {
  createVerticalLinearGradient,
  hexToRGBA,
} from 'react-stockcharts/lib/utils';

import './index.scss';

const SETTINGS = {
  COLOR_BLUE: '#359ee4',
  COLOR_WHITE: '#ffffff',
  COLOR_GREEN: '#37a77d',
  COLOR_RED: '#e84855',
  COLOR_TOOLTIP_BORDER: '#555555',
  MD_SIZE: 14,
  SM_SIZE: 10,
  FOOT_HEIGHT: 15,
  BAR_HEIGHT: 50,
  AREA_HEIGHT: 150,
  AREA_STROKE_WIDTH: 1.5,
};

const buGradient = createVerticalLinearGradient([
  { stop: 1, color: hexToRGBA(SETTINGS.COLOR_GREEN, 1) },
  { stop: 0.5, color: hexToRGBA(SETTINGS.COLOR_GREEN, 0.7) },
  { stop: 0, color: hexToRGBA(SETTINGS.COLOR_GREEN, 0.5) },
]);

const sdGradient = createVerticalLinearGradient([
  { stop: 1, color: hexToRGBA(SETTINGS.COLOR_RED, 1) },
  { stop: 0.5, color: hexToRGBA(SETTINGS.COLOR_RED, 0.7) },
  { stop: 0, color: hexToRGBA(SETTINGS.COLOR_RED, 0.5) },
]);

const MARGIN = { left: 40, right: 40, top: 20, bottom: 30 };

const tickIntervalFunction = (min, max, tickInterval) => {
  if (min < 0) {
    tickInterval += 1;
  }
  const step = Math.abs((max - min) / tickInterval);
  let i = -1,
    n = Math.max(0, Math.ceil(Math.abs(max - min) / step)) | 0,
    range = new Array(n);
  while (++i <= n) {
    range[i] = min + i * step;
  }
  return range;
};

const TICKS = 6;
const TICK_INTERVAL = 5;
const FONT_SIZE = 10;
const RATIO = 0.5;

const TYPE_BU = 1;
const TYPE_SD = 2;

class BusdChart extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      renderKey: 0,
      dataChart: this.getChartData(this.props),
    };
  }

  componentDidMount() {
    this.subscribeRealtime();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { currentSearch } = this.props;
    if (currentSearch !== prevProps.currentSearch) {
      this.unsubscribeAll();
      this.subscribeRealtime();
    }
  }

  componentWillReceiveProps(nextProps, nextContext) {
    const { data } = this.props;
    if (data !== nextProps.data) {
      this.forceRemount();
      this.setState({
        dataChart: this.getChartData(nextProps),
      });
    }
  }

  componentWillUnmount() {
    this.unsubscribeAll();
    clearTimeout(this.timeoutId);
  }

  subscribeRealtime = () => {
    const { currentSearch } = this.props;
    if (currentSearch.isTicker) {
      MessageHub.subscribe(ChanelConfig.TickChanel, this.onReceiveTicker);
    } else {
      MessageHub.subscribe(ChanelConfig.IndexChannel, this.onReceiveIndex);
    }
  };

  unsubscribeAll = () => {
    MessageHub.unsubscribe(ChanelConfig.TickChanel, this.onReceiveTicker);
    MessageHub.unsubscribe(ChanelConfig.IndexChannel, this.onReceiveIndex);
  };

  isBetweenDate = (date, min, max) => {
    const [hoursMin, minutesMin, secondsMin] = min;
    const [hoursMax, minutesMax, secondsMax] = max;
    const minDate = new Date(date.valueOf());
    const maxDate = new Date(date.valueOf());
    minDate.setHours(hoursMin, minutesMin, secondsMin);
    maxDate.setHours(hoursMax, minutesMax, secondsMax);

    return (
      minDate.getTime() < date.getTime() && date.getTime() < maxDate.getTime()
    );
  };

  getNearestIndex = tradingDate => {
    const { dataChart } = this.state;
    const date = new Date(tradingDate);
    const hours = date.getHours();
    const minute = date.getMinutes();
    if (this.isBetweenDate(date, [14, 29, 59], [14, 30, 29])) {
      return 22; //Point 14h30
    }

    if (this.isBetweenDate(date, [11, 29, 0], [12, 0, 0])) {
      return 10; //Point 11h30
    }

    if (hours >= 15) {
      return dataChart.length - 1;
    }

    const exactIndex = dataChart.findIndex(item => {
      const [h, m] = item.time.split(':');

      return +h === +hours && +minute === +m;
    });

    if (exactIndex !== -1) {
      return exactIndex + 1;
    }

    const index = dataChart
      .concat()
      .reverse()
      .findIndex(item => {
        const [h, m] = item.time.split(':');

        return +h === +hours && +minute > +m;
      });
    if (index === -1) {
      return -1;
    }

    if (index === 0) {
      return +minute === 0 ? dataChart.length - 1 : -1;
    }

    return dataChart.length - index;
  };

  onReceiveTicker = tickers => {
    const { dataChart } = this.state;
    const { currentSearch } = this.props;
    const ticker = tickers.find(item => item.organCode === currentSearch.code);
    if (!ticker) {
      return;
    }
    const nearestIndex = this.getNearestIndex(ticker.tradingDate);
    if (nearestIndex === -1) {
      return;
    }
    const oldTick = dataChart[nearestIndex];
    const additionalBu = ticker.matchType === TYPE_BU ? ticker.matchVolume : 0;
    const additionalSd = ticker.matchType === TYPE_SD ? ticker.matchVolume : 0;
    const tickData = {
      matchPrice: ticker.matchPrice,
      volumeBu: (oldTick.volumeBu || 0) + additionalBu,
      volumeSd: (oldTick.volumeSd || 0) + additionalSd,
      time: oldTick.time,
    };
    const newDataChart = dataChart.map((item, index) => {
      if (index === nearestIndex) {
        return tickData;
      }

      return item;
    });
    this.setState({
      dataChart: newDataChart,
    });
  };

  onReceiveIndex = indexs => {
    const { dataChart } = this.state;
    const { currentSearch } = this.props;
    const index = indexs.find(item => item.comGroupCode === currentSearch.code);
    if (!index) {
      return;
    }
    const nearestIndex = this.getNearestIndex(index.tradingDate);
    if (nearestIndex === -1) {
      return;
    }
    const newDataChart = dataChart.map((item, i) => {
      if (i === nearestIndex) {
        return {
          time: item.time,
          matchPrice: index.indexValue,
          volumeBu: index.volumeBu,
          volumeSd: index.VolumeSd,
        };
      }

      return item;
    });
    this.setState({
      dataChart: newDataChart,
    });
  };

  getYExtends = () => {
    const { dataChart: data } = this.state;
    const values = [];
    data.forEach(item => {
      if (item.volumeSd !== undefined) {
        values.push(item.volumeSd);
      }
      if (item.volumeBu !== undefined) {
        values.push(item.volumeBu);
      }
    });

    return [Math.min(...values), Math.max(...values) * 1.1];
  };

  getPriceMaxMin = () => {
    const { dataChart: data } = this.state;
    const { priceData } = this.props;
    const values = [];
    data.forEach(item => {
      if (item.matchPrice !== undefined) {
        values.push(item.matchPrice);
      }
    });
    const min = Math.min(...values);
    let max = Math.max(...values);
    if (min === max) {
      max = (priceData || {}).ceilingPrice || 9999;
    }

    return [min, max];
  };

  getPriceTickValues = () => {
    const [min, max] = this.getPriceMaxMin();

    const step = Math.abs((max - min) / 3);
    const beforeMin = [];
    const afterMin = [];
    for (let i = 0; i <= 3; i++) {
      afterMin.push(min + step * i);
    }
    beforeMin.push(min - step * 2);
    beforeMin.push(min - step);

    return [...beforeMin, ...afterMin];
  };

  getYGrid = () => {
    const margin = this.getDynamicMargin();
    const gridWidth = this.getWidth() - margin.left - margin.right;

    return {
      innerTickSize: -1 * gridWidth,
      tickStrokeDasharray: 'Solid',
      tickStrokeWidth: 1,
      tickStrokeOpacity: 0.2,
      opacity: 0.2,
      strokeWidth: 1,
    };
  };

  getTimeData = props => {
    const { data } = props;
    const map = {};
    data.forEach(item => {
      const time = moment(item.tradingDate).format('HH:mm');
      map[time] = item;
    });

    return map;
  };

  getPoints = (step = 15) => {
    const points = [];
    const start = new Date();
    const end = new Date();
    start.setHours(9, 0);
    end.setHours(15, 0);
    while (start.getTime() <= end.getTime()) {
      const time = moment(start).format('HH:mm');
      points.push(time);
      start.setMinutes(start.getMinutes() + step);
    }

    return points;
  };

  isBreak = date => {
    const breakTime = ['11:31:00', '13:01:00'];

    return isBetweenTime(date, breakTime);
  };

  getChartData = props => {
    const { data } = props;
    const sortedData = data
      .concat()
      .sort((a, b) => (a.tradingDate > b.tradingDate ? 1 : -1));
    const lastPoint = sortedData[sortedData.length - 1];
    const lastPointTime = lastPoint
      ? moment(lastPoint.tradingDate).format('HH:mm')
      : '';
    const finalData = [];
    const mapTimeData = this.getTimeData(props);
    const points = this.getPoints();

    points.forEach(point => {
      const nullPoint = {
        matchPrice: undefined,
        volumeBu: undefined,
        volumeSd: undefined,
        time: point,
      };
      if (mapTimeData[point]) {
        finalData.push({
          ...mapTimeData[point],
          time: point,
        });
        return;
      }
      const [hour, minute] = point.split(':');
      const date = new Date();
      date.setHours(+hour, +minute);
      const now = moment().format('HH:mm');
      const isUsePrevPoint = lastPointTime > now && this.isBreak(date);
      const prevData = finalData[finalData.length - 1] || nullPoint;
      if (isUsePrevPoint) {
        finalData.push({ ...prevData, time: point });
        return;
      }

      if (now > point) {
        finalData.push({
          ...prevData,
          volumeBu: 0,
          volumeSd: 0,
          time: point,
        });
        return;
      }

      const lastPointData = data[data.length - 1];
      if (
        lastPointData &&
        point < moment(lastPointData.tradingDate).format('HH:mm') &&
        !mapTimeData[point]
      ) {
        finalData.push({
          ...prevData,
          volumeBu: 0,
          volumeSd: 0,
          time: point,
        });
        return;
      }

      finalData.push(nullPoint);
    });

    return finalData;
  };

  getIndexTickValues = () => {
    const tickValues = ['09:00', '11:30', '13:00', '15:00'];
    const points = this.getPoints();

    return tickValues.map(tick => points.indexOf(tick));
  };

  getWidth = () => {
    const { width } = this.props;

    return width; //minus padding
  };

  tooltipCanvas = (_ref5, content, ctx, pointer, height, moreProps, _this) => {
    const LINE_SPACE = 3;

    const X = 10;
    const Y = 2;
    var fontSize = 10,
      fontFill = _ref5.fontFill;
    var startY = Y;
    for (var i = 0; i < content.y.length; i++) {
      var y = content.y[i];
      var textY = startY + fontSize * (i + 1) + (i + 1) * LINE_SPACE;
      ctx.fillStyle = y.stroke || fontFill;
      ctx.fillText(y.label, X, textY);
      ctx.fillStyle = fontFill;
      ctx.fillText(y.value, X + ctx.measureText(y.label).width, textY);
    }
  };

  tooltipContent = () => {
    const { yAxisRightTickFormat, thirdLegendKey } = this.props;
    return ({ currentItem }) => {
      if (currentItem.matchPrice !== undefined) {
        return {
          y: [
            {
              label: `${I18n.t('common.buyUp')}: `,
              value: formatTextFloat(formatVolume(currentItem.volumeBu)),
            },
            {
              label: `${I18n.t('common.sellDown')}: `,
              value: formatTextFloat(formatVolume(currentItem.volumeSd)),
            },
            {
              label: `${I18n.t(thirdLegendKey)}: `,
              value: yAxisRightTickFormat(currentItem.matchPrice),
            },
            {
              label: `${I18n.t('common.time')}: `,
              value: currentItem.time,
            },
          ],
        };
      }
    };
  };

  getMax = () => {
    const { dataChart: data } = this.state;
    if (!data.length) {
      return {
        leftMax: 0,
        rightMax: 0,
      };
    }
    let leftMax =
      data[0].volumeBu > data[0].volumeSd ? data[0].volumeBu : data[0].volumeSd;
    let rightMax = data[0].matchPrice || 0;
    data.forEach(item => {
      if (item.volumeBu > leftMax) {
        leftMax = item.volumeBu;
      }
      if (item.volumeSd > leftMax) {
        leftMax = item.volumeSd;
      }
      if (item.matchPrice > rightMax) {
        rightMax = item.matchPrice;
      }
    });

    if (!leftMax) {
      leftMax = 0;
    }

    if (!rightMax) {
      rightMax = 0;
    }

    return {
      leftMax,
      rightMax,
    };
  };

  getDynamicMargin = () => {
    const { leftMax, rightMax } = this.getMax();

    const widthPerLetter = 12;
    return {
      ...MARGIN,
      left: Math.max(MARGIN.left, leftMax.toString().length * widthPerLetter),
      right: Math.max(
        MARGIN.right,
        rightMax.toString().length * widthPerLetter,
      ),
    };
  };

  getTickColor = () => {
    const { theme } = this.props;

    return theme === THEMES.LIGHT ? '#444444' : '#ffffff';
  };

  forceRemount = () => {
    this.setState({
      renderKey: this.state.renderKey + 1,
    });
  };

  leftTickFormat = d => {
    const { leftMax } = this.getMax();
    const digits = leftMax >= 10000 ? 0 : 2;

    return formatTextFloat(formatVolume(d), digits);
  };

  render() {
    const { renderKey, dataChart } = this.state;
    const {
      data,
      title,
      height: propHeight,
      yAxisRightTickFormat,
      rightI18nKey,
      thirdLegendKey,
      locale,
    } = this.props;
    const tickColor = this.getTickColor();
    const margin = this.getDynamicMargin();
    if (data.length < 2) {
      return <div>{I18n.t('common.noData')}</div>;
    }
    const width = this.getWidth();
    const points = this.getPoints();
    const chartHeight = propHeight || width * RATIO;
    const priceTickValues = this.getPriceTickValues();
    return (
      <div key={`${width}_${renderKey}`} className="bu-sd-chart">
        <div>
          <div
            style={{
              width: chartHeight,
              left: `-${chartHeight / 2 - 20}px`,
            }}
            className="y-axis-left-title y-axis-title"
          >
            {I18n.t('common.busd.volume')}
          </div>
          <div
            style={{ width: chartHeight, right: `-${chartHeight / 2 - 20}px` }}
            className="y-axis-right-title y-axis-title"
          >
            {I18n.t(rightI18nKey)}
          </div>
          <div className="title-chart">{title}</div>
          <ChartCanvas
            ratio={window.devicePixelRatio}
            width={width}
            height={chartHeight}
            margin={margin}
            data={dataChart}
            type={'hybrid'}
            yScale={scaleLinear()}
            xScale={scalePoint()}
          >
            <Chart id={0} yExtents={this.getYExtends()}>
              <XAxis
                showDomain={false}
                tickStroke={tickColor}
                tickStrokeOpacity={0.2}
                opacity={0.2}
                stroke={tickColor}
                axisAt="bottom"
                orient="bottom"
                showTick={true}
                fontSize={FONT_SIZE}
                zoomEnabled={false}
                tickValues={this.getIndexTickValues()}
                tickFormat={index => points[index]}
              />
              <YAxis
                showDomain={false}
                showTick={false}
                zoomEnabled={false}
                fontSize={FONT_SIZE}
                tickFormat={this.leftTickFormat}
                tickStroke={tickColor}
                stroke={tickColor}
                ticks={TICKS}
                axisAt="left"
                orient="left"
                tickInterval={TICK_INTERVAL}
                tickIntervalFunction={tickIntervalFunction}
                {...this.getYGrid()}
              />
              {/*BU series*/}
              <AreaSeries
                strokeWidth={SETTINGS.AREA_STROKE_WIDTH}
                stroke="#37a77d"
                canvasGradient={buGradient}
                yAccessor={d => d.volumeBu}
              />
              <AreaSeries
                strokeWidth={SETTINGS.AREA_STROKE_WIDTH}
                stroke="#e84855"
                canvasGradient={sdGradient}
                yAccessor={d => d.volumeSd}
              />
              <HoverTooltip
                bgFixedSize={{
                  width: locale === listLanguage.en ? 110 : 160,
                  height: 60,
                }}
                bgOpacity={0.1}
                fill={SETTINGS.COLOR_WHITE}
                stroke={SETTINGS.COLOR_TOOLTIP_BORDER}
                opacity={1}
                yAccessor={d => d.volumeBu}
                fontSize={SETTINGS.MD_SIZE}
                tooltipContent={this.tooltipContent()}
                tooltipCanvas={this.tooltipCanvas}
              />
            </Chart>

            <Chart
              id={1}
              yExtents={[
                priceTickValues[0],
                priceTickValues[priceTickValues.length - 1],
              ]}
            >
              <YAxis
                showDomain={false}
                zoomEnabled={false}
                tickStroke="#ffffff"
                stroke="#ffffff"
                axisAt="right"
                ticks={TICKS}
                tickStrokeOpacity={0.2}
                opacity={0.2}
                fontSize={FONT_SIZE}
                tickInterval={TICK_INTERVAL}
                tickFormat={yAxisRightTickFormat}
                orient="right"
                tickValues={priceTickValues}
              />
              <LineSeries
                strokeWidth={SETTINGS.AREA_STROKE_WIDTH}
                stroke={SETTINGS.COLOR_BLUE}
                yAccessor={d => d.matchPrice}
                strokeDasharray="Solid"
              />
            </Chart>
          </ChartCanvas>

          <div className="legends">
            <div className="bu item">{I18n.t('common.buyUp')}</div>
            <div className="sd item">{I18n.t('common.sellDown')}</div>
            <div className="price item">{I18n.t(thirdLegendKey)}</div>
          </div>
        </div>
      </div>
    );
  }
}

BusdChart.defaultProps = {
  currentSearch: PropTypes.object,
};

BusdChart.defaultProps = {
  title: '',
  yAxisRightTickFormat: d => formatTextFloat(formatPrice(d)),
  rightI18nKey: 'common.busd.price',
  thirdLegendKey: 'common.price',
};

const mapStateToProps = state => ({
  locale: state.i18n.locale,
  theme: state.theme,
  timeOffset: state[COMMON_REDUCER_NAME].timeOffset,
});

export default connect(mapStateToProps)(BusdChart);
